import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { DetailTreeNav, type TreeNode } from '@/components/layout/DetailTreeNav';
import { PortalViewButton } from '@/components/portal/PortalViewButton';
import { PortalUsagePanel } from '@/components/portal/PortalUsagePanel';
import { createInvite } from '@/lib/api/invites';
import { clientPath, clientSectionPath, estimatePath, jobPath, labourEstimatePath, quotePath } from '@/lib/utils/navPaths';
import { getClients, createClient, updateClient, deleteClient, getClientById } from '@/lib/api/clients';
import type { Client } from '@/lib/api/clients';
import { ClientsTable } from '@/components/clients/ClientsTable';
import { ClientDocumentsSection } from '@/components/clients/ClientDocumentsSection';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer, DetailSection, LargeTitleHeader, PullToRefresh, SwipeableRow } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import apiClient from '@/lib/api/client';
import { createQuoteFromEstimate, getQuotesByClient, type Quote } from '@/lib/api/quotes';
import { QuoteBuilderModal } from '@/components/quotes/QuoteBuilderModal';
import {
  getInvoicesByClient,
  getInvoice,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  sendInvoice,
  recordPayment,
  createInvoiceFromQuote,
  invoicePath,
  type Invoice,
  type InvoiceDetail,
  type CreateInvoiceData,
  type UpdateInvoiceData,
  type RecordPaymentData,
} from '@/lib/api/invoices';
import {
  InvoiceModal,
  InvoiceDetailModal,
  RecordPaymentModal,
  InvoiceStatusBadge,
} from '@/components/invoices';

type Job = {
  id: string;
  job_number: string;
  title: string;
  status: string;
  total_price?: number;
  start_date?: string;
  end_date?: string;
};

// The old left-panel SwipeableRow list is superseded by the jobs-style main-area
// ClientsTable; kept compiled-but-disabled for reference/rollback.
const LEGACY_LIST_PANEL: boolean = false;

type ClientData = {
  id: string;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
  jobCount?: number;
  totalRevenue?: number;
  activeJobs?: number;
  completedJobs?: number;
  jobs?: Job[];
  quotes?: Quote[];
};

export const ClientsPage = () => {
  // ?new=1 auto-open (dashboard quick actions)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('new') === '1') {
      handleOpenModal();
      sp.delete('new');
      window.history.replaceState(null, '', window.location.pathname + (sp.toString() ? '?' + sp.toString() : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const navigate = useNavigate();
  const { id: urlClientId, section: urlSection } = useParams<{ id?: string; section?: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
  });
  const [estimates, setEstimates] = useState<any[]>([]);
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  const [labourEstimates, setLabourEstimates] = useState<any[]>([]);
  const [loadingLabourEstimates, setLoadingLabourEstimates] = useState(false);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [showLabourModal, setShowLabourModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showEstimateSelectorForQuote, setShowEstimateSelectorForQuote] = useState(false);
  const [creatingQuoteFromEstimate, setCreatingQuoteFromEstimate] = useState(false);
  const [selectedEstimateIds, setSelectedEstimateIds] = useState<number[]>([]);
  const [selectedLabourEstimateIds, setSelectedLabourEstimateIds] = useState<number[]>([]);
  const [selectedJobIdForQuote, setSelectedJobIdForQuote] = useState<string>('');
  const [showQuoteBuilder, setShowQuoteBuilder] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  // Invoice state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetail | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<(Invoice & { items?: any[] }) | null>(null);
  const [labourFormData, setLabourFormData] = useState({
    title: '',
    job_id: '',
    total_hours: '',
    hourly_rate: '75',
    notes: ''
  });
  const [materialFormData, setMaterialFormData] = useState({
    title: '',
    job_id: '',
    items: [{ name: '', quantity: '', unit_price: '' }],
    notes: ''
  });
  const { toasts, removeToast, toast } = useToast();
  const [confirmDeleteInvoice, setConfirmDeleteInvoice] = useState(false);
  const [confirmDeleteClientId, setConfirmDeleteClientId] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const clientSearchRef = useRef<HTMLInputElement>(null);
  // Detail-tree navigation: the section of the selected client is URL-driven
  // (/clients/:id/:section) so sections are deep-linkable and Back traverses them.
  const CLIENT_SECTIONS = ['contact', 'jobs', 'notes', 'quotes', 'estimates', 'invoices', 'documents', 'usage'];
  const activeSection = urlSection && CLIENT_SECTIONS.includes(urlSection) ? urlSection : 'contact';
  // When the user explicitly backs out to "All clients", suppress auto-selecting the first one.
  const [forceList, setForceList] = useState(false);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 1500);
    }).catch(() => {});
  };

  // Export clients list as CSV (#21)
  const exportClientsCsv = () => {
    const rows = [
      ['Name', 'Company', 'Email', 'Phone', 'Address'],
      ...filteredClients.map(c => [
        c.name ?? '',
        (c as any).company ?? '',
        c.email ?? '',
        c.phone ?? '',
        c.address ?? '',
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success('Client list exported');
  };

  // Keyboard shortcuts: "n" = new client, "/" = focus search, Esc = close modal (#7)
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement).isContentEditable;
    if (e.key === 'Escape') {
      if (isModalOpen) handleCloseModal();
      return;
    }
    if (isInput) return;
    if (e.key === 'n' || e.key === 'N') {
      e.preventDefault();
      handleOpenModal();
    } else if (e.key === '/') {
      e.preventDefault();
      clientSearchRef.current?.focus();
    }
  }, [isModalOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (urlClientId) {
      loadClientDetails(urlClientId);
    } else {
      // /clients with no id = the full list view (no auto-jump to first client)
      setSelectedClient(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlClientId]);

  const loadClientDetails = async (clientId: string) => {
    try {
      const data = await getClientById(clientId);
      setSelectedClient(data as ClientData);
      if (!urlClientId) {
        navigate(clientPath(clientId), { replace: true });
      }
      // Load estimates, quotes, and invoices when client is selected
      loadEstimates(clientId);
      loadLabourEstimates(clientId);
      loadQuotes(clientId);
      loadInvoices(clientId);
    } catch (error) {
      console.error('Failed to load client details:', error);
      toast.error('Failed to load client details');
    }
  };

  const loadEstimates = async (clientId: string) => {
    try {
      setLoadingEstimates(true);
      const response = await apiClient.get(`/estimates?client_id=${clientId}`);
      const estimatesData = response.data?.data || response.data || [];
      setEstimates(Array.isArray(estimatesData) ? estimatesData : []);
    } catch (error) {
      console.error('🔴 Failed to load estimates:', error);
      setEstimates([]);
    } finally {
      setLoadingEstimates(false);
    }
  };

  const loadLabourEstimates = async (clientId: string) => {
    try {
      setLoadingLabourEstimates(true);
      const response = await apiClient.get(`/labour-estimates?client_id=${clientId}`);
      const data = response.data?.data || response.data || [];
      setLabourEstimates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load labour estimates:', error);
      setLabourEstimates([]);
    } finally {
      setLoadingLabourEstimates(false);
    }
  };

  const loadQuotes = async (clientId: string) => {
    try {
      setLoadingQuotes(true);
      const quotesData = await getQuotesByClient(clientId);
      setQuotes(Array.isArray(quotesData) ? quotesData : []);
    } catch (error) {
      console.error('Failed to load quotes:', error);
      setQuotes([]);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const loadInvoices = async (clientId: string) => {
    try {
      setLoadingInvoices(true);
      const invoicesData = await getInvoicesByClient(parseInt(clientId));
      setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleViewInvoice = async (invoiceId: number) => {
    try {
      const invoiceDetail = await getInvoice(invoiceId);
      setSelectedInvoice(invoiceDetail);
      setShowInvoiceDetailModal(true);
    } catch (error) {
      console.error('Failed to load invoice details:', error);
      toast.error('Failed to load invoice details');
    }
  };

  const handleCreateInvoice = () => {
    setEditingInvoice(null);
    setShowInvoiceModal(true);
  };

  const handleEditInvoice = async () => {
    if (selectedInvoice) {
      setEditingInvoice({
        ...selectedInvoice,
        items: selectedInvoice.items,
      });
      setShowInvoiceDetailModal(false);
      setShowInvoiceModal(true);
    }
  };

  const handleSaveInvoice = async (data: CreateInvoiceData | UpdateInvoiceData, invoiceId?: number) => {
    try {
      if (invoiceId) {
        await updateInvoice(invoiceId, data as UpdateInvoiceData);
        toast.success('Invoice updated successfully');
      } else {
        await createInvoice(data as CreateInvoiceData);
        toast.success('Invoice created successfully');
      }
      setShowInvoiceModal(false);
      setEditingInvoice(null);
      if (selectedClient?.id) {
        loadInvoices(selectedClient.id);
      }
    } catch (error) {
      console.error('Failed to save invoice:', error);
      throw error;
    }
  };

  const handleDeleteInvoice = () => {
    if (!selectedInvoice) return;
    setConfirmDeleteInvoice(true);
  };

  const confirmDeleteInvoiceAction = async () => {
    if (!selectedInvoice) return;
    setConfirmDeleteInvoice(false);
    try {
      await deleteInvoice(selectedInvoice.id);
      toast.success('Invoice deleted successfully');
      setShowInvoiceDetailModal(false);
      setSelectedInvoice(null);
      if (selectedClient?.id) {
        loadInvoices(selectedClient.id);
      }
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const handleSendInvoice = async () => {
    if (!selectedInvoice) return;
    try {
      await sendInvoice(selectedInvoice.id);
      toast.success('Invoice marked as sent');
      // Reload invoice detail
      const invoiceDetail = await getInvoice(selectedInvoice.id);
      setSelectedInvoice(invoiceDetail);
      if (selectedClient?.id) {
        loadInvoices(selectedClient.id);
      }
    } catch (error) {
      console.error('Failed to send invoice:', error);
      toast.error('Failed to send invoice');
    }
  };

  const handleRecordPayment = () => {
    setShowPaymentModal(true);
  };

  const handleSubmitPayment = async (data: RecordPaymentData) => {
    if (!selectedInvoice) return;
    try {
      await recordPayment(selectedInvoice.id, data);
      toast.success('Payment recorded successfully');
      // Reload invoice detail
      const invoiceDetail = await getInvoice(selectedInvoice.id);
      setSelectedInvoice(invoiceDetail);
      setShowPaymentModal(false);
      if (selectedClient?.id) {
        loadInvoices(selectedClient.id);
      }
    } catch (error) {
      console.error('Failed to record payment:', error);
      throw error;
    }
  };

  const handleCreateInvoiceFromQuote = async (quoteId: string) => {
    try {
      const result = await createInvoiceFromQuote(parseInt(quoteId));
      toast.success(`Invoice ${result.invoiceNumber} created from quote`);
      if (selectedClient?.id) {
        loadInvoices(selectedClient.id);
      }
      // Open the newly created invoice
      handleViewInvoice(result.id);
    } catch (error: any) {
      console.error('Failed to create invoice from quote:', error);
      toast.error(error.response?.data?.message || 'Failed to create invoice from quote');
    }
  };

  const handleCreateLabourEstimate = async () => {
    if (!labourFormData.title || !labourFormData.job_id || !labourFormData.total_hours || !labourFormData.hourly_rate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const totalAmount = parseFloat(labourFormData.total_hours) * parseFloat(labourFormData.hourly_rate);
      await apiClient.post('/labour-estimates', {
        title: labourFormData.title,
        client_id: parseInt(selectedClient?.id || '0'),
        job_id: parseInt(labourFormData.job_id),
        total_hours: parseFloat(labourFormData.total_hours),
        hourly_rate: parseFloat(labourFormData.hourly_rate),
        total_amount: totalAmount,
        notes: labourFormData.notes
      });
      
      toast.success('Labour estimate created successfully!');
      setShowLabourModal(false);
      setLabourFormData({ title: '', job_id: '', total_hours: '', hourly_rate: '75', notes: '' });
      if (selectedClient?.id) {
        loadLabourEstimates(selectedClient.id);
      }
    } catch (error) {
      console.error('Failed to create labour estimate:', error);
      toast.error('Failed to create labour estimate');
    }
  };

  const handleCreateMaterialEstimate = async () => {
    if (!materialFormData.title || !materialFormData.job_id) {
      toast.error('Please fill in title and select a job');
      return;
    }

    const validItems = materialFormData.items.filter(item => item.name && item.quantity && item.unit_price);
    if (validItems.length === 0) {
      toast.error('Please add at least one material item');
      return;
    }

    try {
      const items = validItems.map(item => ({
        product_id: null,
        product_name: item.name,
        quantity: parseFloat(item.quantity),
        unit_price: parseFloat(item.unit_price),
        total: parseFloat(item.quantity) * parseFloat(item.unit_price)
      }));

      const total = items.reduce((sum, item) => sum + item.total, 0);

      await apiClient.post('/estimates', {
        title: materialFormData.title,
        client_id: parseInt(selectedClient?.id || '0'),
        job_id: parseInt(materialFormData.job_id),
        items,
        total
      });
      
      toast.success('Material estimate created successfully!');
      setShowMaterialModal(false);
      setMaterialFormData({ title: '', job_id: '', items: [{ name: '', quantity: '', unit_price: '' }], notes: '' });
      if (selectedClient?.id) {
        loadEstimates(selectedClient.id);
      }
    } catch (error) {
      console.error('Failed to create material estimate:', error);
      toast.error('Failed to create material estimate');
    }
  };

  const addMaterialItem = () => {
    setMaterialFormData({
      ...materialFormData,
      items: [...materialFormData.items, { name: '', quantity: '', unit_price: '' }]
    });
  };

  const removeMaterialItem = (index: number) => {
    if (materialFormData.items.length > 1) {
      setMaterialFormData({
        ...materialFormData,
        items: materialFormData.items.filter((_, i) => i !== index)
      });
    }
  };

  const updateMaterialItem = (index: number, field: string, value: string) => {
    const newItems = [...materialFormData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setMaterialFormData({ ...materialFormData, items: newItems });
  };

  const handleCreateQuoteFromEstimates = async () => {
    if (!selectedJobIdForQuote || (selectedEstimateIds.length === 0 && selectedLabourEstimateIds.length === 0)) {
      toast.error('Please select a job and at least one estimate');
      return;
    }
    try {
      setCreatingQuoteFromEstimate(true);
      const newQuote = await createQuoteFromEstimate(
        selectedEstimateIds, 
        selectedLabourEstimateIds,
        parseInt(selectedJobIdForQuote)
      );
      setShowEstimateSelectorForQuote(false);
      setSelectedEstimateIds([]);
      setSelectedLabourEstimateIds([]);
      const jobForQuote = selectedJobIdForQuote;
      setSelectedJobIdForQuote('');
      toast.success('Quote created from estimates successfully!');
      // Unified quote destination (clients › jobs › quotes)
      navigate(quotePath(selectedClient?.id, jobForQuote, newQuote.id));
    } catch (error) {
      console.error('Failed to create quote from estimates:', error);
      toast.error('Failed to create quote from estimates');
    } finally {
      setCreatingQuoteFromEstimate(false);
    }
  };

  const handleOpenQuoteBuilder = (quoteId: string) => {
    const q = quotes.find(x => String(x.id) === String(quoteId));
    navigate(quotePath(selectedClient?.id, q?.job_id, quoteId)); // unified quote page
  };

  const handleCloseQuoteBuilder = () => {
    setShowQuoteBuilder(false);
    setSelectedQuoteId(null);
    // Reload quotes after closing
    if (selectedClient?.id) {
      loadQuotes(selectedClient.id);
    }
  };

  const toggleEstimateSelection = (estimateId: number) => {
    setSelectedEstimateIds(prev =>
      prev.includes(estimateId) 
        ? prev.filter(id => id !== estimateId)
        : [...prev, estimateId]
    );
  };

  const toggleLabourEstimateSelection = (estimateId: number) => {
    setSelectedLabourEstimateIds(prev => 
      prev.includes(estimateId) 
        ? prev.filter(id => id !== estimateId)
        : [...prev, estimateId]
    );
  };

  const getEstimatesForJob = (jobId: string) => {
    return estimates.filter((est: any) => String(est.job_id) === jobId);
  };

  const getLabourEstimatesForJob = (jobId: string) => {
    return labourEstimates.filter((est: any) => String(est.job_id) === jobId);
  };

  const getUniqueJobs = () => {
    const jobMap = new Map();
    // Add jobs from material estimates
    estimates.forEach((est: any) => {
      if (est.job_id && !jobMap.has(String(est.job_id))) {
        jobMap.set(String(est.job_id), {
          id: String(est.job_id),
          title: est.job_title || `Job #${est.job_id}`
        });
      }
    });
    // Add jobs from labour estimates
    labourEstimates.forEach((est: any) => {
      if (est.job_id && !jobMap.has(String(est.job_id))) {
        jobMap.set(String(est.job_id), {
          id: String(est.job_id),
          title: est.job_title || `Job #${est.job_id}`
        });
      }
    });
    return Array.from(jobMap.values());
  };

  const handleClientClick = (clientId: string) => {
    setForceList(false);
    loadClientDetails(clientId);
    navigate(`/clients/${clientId}`);
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await getClients();
      setClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const data = await getClients(searchQuery);
      setClients(data);
    } catch (error) {
      console.error('Failed to search clients:', error);
      toast.error('Failed to search clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery) {
        handleSearch();
      } else {
        loadClients();
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleOpenModal = (client?: Client) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name,
        company: client.company || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        notes: client.notes || '',
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        address: '',
        notes: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    setFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      address: '',
      notes: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await updateClient(editingClient.id, formData);
        toast.success('Client updated successfully');
      } else {
        await createClient(formData);
        toast.success('Client created successfully');
      }
      handleCloseModal();
      loadClients();
    } catch (error) {
      console.error('Failed to save client:', error);
      toast.error('Failed to save client');
    }
  };

  const handleDelete = (id: string) => {
    setConfirmDeleteClientId(id);
  };

  const confirmDeleteClientAction = async () => {
    if (!confirmDeleteClientId) return;
    const id = confirmDeleteClientId;
    setConfirmDeleteClientId(null);
    try {
      await deleteClient(id);
      toast.success('Client deleted successfully');
      if (selectedClient?.id === id) {
        setSelectedClient(null);
        navigate('/clients');
      }
      loadClients();
    } catch (error: any) {
      console.error('Failed to delete client:', error);
      toast.error(error.response?.data?.error || 'Failed to delete client');
    }
  };

  const [sendingInvite, setSendingInvite] = useState(false);
  const handleSendClientInvite = async () => {
    if (!selectedClient) return;
    if (!selectedClient.email) { toast.error('This client has no email address. Add one first.'); return; }
    setSendingInvite(true);
    try {
      const invite = await createInvite({
        invite_type: 'client',
        email: selectedClient.email,
        target_client_id: Number(selectedClient.id),
      });
      const inviteUrl = invite.inviteUrl || `${window.location.origin}/portal/register?code=${invite.code}`;
      await navigator.clipboard.writeText(inviteUrl).catch(() => {});
      toast.success(`Portal invite for ${selectedClient.email} — link copied to clipboard.`);
    } catch {
      toast.error('Failed to create invite.');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleEditCurrent = () => {
    if (selectedClient) {
      handleOpenModal(selectedClient as Client);
    }
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
      case 'in progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const filteredClients = clients;

  // Show the section tree once a client is open; the client list is reachable via "All clients".
  const showTree = !!(selectedClient && urlClientId && !forceList);

  const clientTreeNodes: TreeNode[] = [
    { key: 'contact', label: 'Overview', icon: 'contact_mail' },
    {
      key: 'jobs', label: 'Jobs', icon: 'work',
      count: selectedClient?.jobs?.length ?? 0,
      children: selectedClient?.jobs?.map(j => ({ key: `job:${j.id}`, label: j.title || j.job_number, icon: 'construction' })),
    },
    { key: 'notes', label: 'Notes', icon: 'note' },
    {
      key: 'quotes', label: 'Quotes', icon: 'description',
      count: quotes.length,
      children: quotes.map(q => ({ key: `quote:${q.id}`, label: q.quote_number || `Quote #${q.id}`, icon: 'request_quote' })),
    },
    { key: 'estimates', label: 'Estimates', icon: 'assessment', count: estimates.length + labourEstimates.length },
    {
      key: 'invoices', label: 'Invoices', icon: 'receipt',
      count: invoices.length,
      children: invoices.map(inv => ({ key: `invoice:${inv.id}`, label: inv.invoice_number || `Invoice #${inv.id}`, icon: 'receipt_long' })),
    },
    { key: 'documents', label: 'Documents', icon: 'article' },
    { key: 'usage', label: 'Portal Usage', icon: 'monitoring' },
  ];

  // Section nodes set the active main-panel view; record nodes drill into the existing detail UI.
  const handleTreeSelect = (key: string) => {
    if (key.startsWith('invoice:')) {
      const id = parseInt(key.slice('invoice:'.length));
      const inv = invoices.find(i => i.id === id);
      if (inv) navigate(invoicePath(inv));   // → unified clients › jobs › invoices destination
    } else if (key.startsWith('quote:')) {
      handleOpenQuoteBuilder(key.slice('quote:'.length));
    } else if (key.startsWith('job:')) {
      navigate(jobPath(selectedClient?.id, key.slice('job:'.length)));
    } else if (selectedClient) {
      // Section selection is URL-driven: /clients/:id/:section (contact = default URL)
      navigate(key === 'contact' ? clientPath(selectedClient.id) : clientSectionPath(selectedClient.id, key));
    }
  };

  return (
    <MainLayout
      clientListPanel={
        showTree ? (
          <DetailTreeNav
            title={selectedClient!.name}
            subtitle={selectedClient!.company || undefined}
            back={{ label: 'All clients', onClick: () => { setForceList(true); setSelectedClient(null); navigate('/clients'); } }}
            nodes={clientTreeNodes}
            activeKey={activeSection}
            onSelect={handleTreeSelect}
            defaultExpanded={['invoices']}
          />
        ) : (LEGACY_LIST_PANEL && (
        <div className="flex flex-col h-full bg-white">
          <div className="px-4 pt-3 pb-2 border-b border-gray-200">
            <LargeTitleHeader
              title="Clients"
              collapseDistance={40}
              actions={
                <div className="flex items-center gap-1.5">
                  {/* Export CSV (#21) */}
                  <button
                    onClick={exportClientsCsv}
                    className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                    title="Export CSV"
                  >
                    <span className="material-symbols-outlined text-base">download</span>
                  </button>
                  <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center justify-center gap-1 rounded-lg h-9 px-3 bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
                    title="New client (N)"
                  >
                    <span className="material-symbols-outlined text-base">add</span>
                    <span className="hidden sm:inline">Add</span>
                  </button>
                </div>
              }
            />
            <div className="flex w-full flex-1 items-stretch rounded-lg h-10 mt-1">
              <div className="text-gray-500 flex border border-r-0 border-gray-300 bg-gray-50 items-center justify-center pl-3 rounded-l-lg">
                <span className="material-symbols-outlined text-base">search</span>
              </div>
              <input
                ref={clientSearchRef}
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-r-lg text-gray-900 focus:outline-0 focus:ring-1 focus:ring-primary border-gray-300 border bg-white h-full placeholder:text-gray-500 px-3 pl-2 text-sm font-normal leading-normal focus:border-primary"
                placeholder="Search clients… (/)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setSearchQuery(''); (e.target as HTMLInputElement).blur(); }}}
              />
            </div>
          </div>
          <PullToRefresh onRefresh={loadClients} className="flex-1 min-h-0">
          <div className="overflow-y-auto h-full">
            {loading ? (
              <div className="p-3 space-y-2">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="flex items-center gap-3 px-2 py-2">
                    <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3.5 bg-gray-100 rounded animate-pulse w-3/4" />
                      <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="p-6 text-center">
                <span className="material-symbols-outlined text-gray-300 text-4xl block mb-2">person_search</span>
                <p className="text-sm text-gray-500">No clients found</p>
                {searchQuery && <p className="text-xs text-gray-400 mt-1">Try a different search term</p>}
              </div>
            ) : (
              filteredClients.map((client) => (
                <SwipeableRow
                  key={client.id}
                  actions={[
                    { key: 'edit', label: 'Edit', icon: 'edit', variant: 'default', onClick: () => handleOpenModal(client as Client) },
                    { key: 'delete', label: 'Delete', icon: 'delete', variant: 'danger', onClick: () => handleDelete(client.id) },
                  ]}
                >
                  <div
                    onClick={() => handleClientClick(client.id)}
                    className={`flex items-center gap-4 px-4 min-h-[72px] py-2 justify-between cursor-pointer border-l-4 transition-colors ${
                      selectedClient?.id === client.id
                        ? 'bg-primary/10 border-primary'
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-primary/10 text-primary rounded-full h-12 w-12 flex items-center justify-center flex-shrink-0 font-bold text-lg">
                        {client.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex flex-col justify-center min-w-0">
                        <p className={`text-sm font-semibold leading-normal line-clamp-1 ${selectedClient?.id === client.id ? 'text-primary' : 'text-gray-900'}`}>
                          {client.name}
                        </p>
                        <p className="text-gray-500 text-xs font-normal leading-normal line-clamp-1">
                          {client.company || client.email || 'No details'}
                        </p>
                      </div>
                    </div>
                  </div>
                </SwipeableRow>
              ))
            )}
          </div>
          </PullToRefresh>
        </div>
        ))
      }
      noPadding
    >
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="flex h-full w-full overflow-hidden">
        {/* Client Details */}
        <div className="flex-1 flex flex-col overflow-y-auto bg-background-light">
          {selectedClient ? (
            <>
              <div className="px-6 pt-0 pb-2 border-b border-gray-200 bg-white">
                <LargeTitleHeader
                  title={selectedClient.name}
                  subtitle={selectedClient.company || 'No company'}
                  actions={
                    <div className="flex items-center gap-1.5">
                      {/* Portal state at a glance (audit: Erik couldn't tell who was invited) */}
                      {(() => {
                        const ps = (selectedClient as { portal_status?: string }).portal_status;
                        const map: Record<string, { label: string; cls: string; title: string }> = {
                          active: { label: 'Portal ✓', cls: 'bg-green-100 text-green-700', title: 'Client has signed in to their portal' },
                          invited: { label: 'Registered', cls: 'bg-blue-100 text-blue-700', title: 'Account created — hasn\'t signed in yet' },
                          provisioned: { label: 'Not invited', cls: 'bg-gray-100 text-gray-500', title: 'Placeholder account only — send an invite so they can sign in' },
                        };
                        const chip = ps ? map[ps] : undefined;
                        return chip ? (
                          <span title={chip.title} className={`hidden sm:inline text-[11px] font-semibold px-2 py-1 rounded-full ${chip.cls}`}>{chip.label}</span>
                        ) : null;
                      })()}
                      <button
                        onClick={handleSendClientInvite}
                        disabled={sendingInvite}
                        title="Send portal login invite to this client"
                        className="flex items-center justify-center gap-1.5 overflow-hidden rounded-lg h-9 px-3 bg-white text-gray-700 text-sm font-semibold border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-60"
                      >
                        <span className={`material-symbols-outlined text-base ${sendingInvite ? 'animate-spin' : ''}`}>{sendingInvite ? 'progress_activity' : 'mail'}</span>
                        <span className="hidden sm:inline">{sendingInvite ? 'Sending…' : 'Invite'}</span>
                      </button>
                      <PortalViewButton type="client" recordId={Number(selectedClient.id)} onError={msg => toast.error(msg)} />
                      <button
                        onClick={handleEditCurrent}
                        className="flex items-center justify-center gap-1.5 overflow-hidden rounded-lg h-9 px-3 bg-white text-gray-700 text-sm font-semibold border border-gray-300 hover:bg-gray-100 transition-colors"
                      >
                        <span className="material-symbols-outlined text-base">edit</span>
                        <span className="hidden sm:inline truncate">Edit</span>
                      </button>
                    </div>
                  }
                />
              </div>
                <div className="flex flex-col flex-1 p-6">
                  <div className="space-y-4">
                    {/* Contact Info Section */}
                    <DetailSection title="Contact Info" icon="contact_mail" sectionKey="contact" activeKey={activeSection} onActivate={handleTreeSelect}>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">Contact Details</h4>
                          <dl className="space-y-3">
                            <div className="flex justify-between items-center group">
                              <dt className="text-sm text-gray-500">Email</dt>
                              <dd className="flex items-center gap-1.5">
                                {selectedClient.email ? (
                                  <>
                                    {/* click-to-email (#8) */}
                                    <a href={`mailto:${selectedClient.email}`} className="text-sm font-medium text-primary hover:underline">{selectedClient.email}</a>
                                    {/* copy email (#9) */}
                                    <button onClick={() => copyToClipboard(selectedClient.email!, 'email')} title="Copy email" className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary p-0.5 rounded">
                                      <span className="material-symbols-outlined text-xs">{copiedField === 'email' ? 'check' : 'content_copy'}</span>
                                    </button>
                                  </>
                                ) : <span className="text-sm text-gray-400">N/A</span>}
                              </dd>
                            </div>
                            <div className="flex justify-between items-center group">
                              <dt className="text-sm text-gray-500">Phone</dt>
                              <dd className="flex items-center gap-1.5">
                                {selectedClient.phone ? (
                                  <>
                                    {/* click-to-call (#8) */}
                                    <a href={`tel:${selectedClient.phone}`} className="text-sm font-medium text-primary hover:underline">{selectedClient.phone}</a>
                                    {/* copy phone (#9) */}
                                    <button onClick={() => copyToClipboard(selectedClient.phone!, 'phone')} title="Copy phone" className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary p-0.5 rounded">
                                      <span className="material-symbols-outlined text-xs">{copiedField === 'phone' ? 'check' : 'content_copy'}</span>
                                    </button>
                                  </>
                                ) : <span className="text-sm text-gray-400">N/A</span>}
                              </dd>
                            </div>
                          </dl>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">Address</h4>
                          <div className="flex items-start gap-2 group">
                            <address className="not-italic text-sm text-gray-800 font-medium flex-1">
                              {selectedClient.address || 'No address provided'}
                            </address>
                            {selectedClient.address && (
                              <button onClick={() => copyToClipboard(selectedClient.address!, 'address')} title="Copy address" className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary p-0.5 rounded flex-shrink-0 mt-0.5">
                                <span className="material-symbols-outlined text-xs">{copiedField === 'address' ? 'check' : 'content_copy'}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </DetailSection>

                    {/* Jobs Section */}
                    <DetailSection title="Jobs" icon="work" sectionKey="jobs" activeKey={activeSection} onActivate={handleTreeSelect}>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                          <thead className="text-xs uppercase bg-gray-50 text-gray-600">
                            <tr>
                              <th className="px-6 py-3">Job #</th>
                              <th className="px-6 py-3">Title</th>
                              <th className="px-6 py-3">Status</th>
                              <th className="px-6 py-3">Start Date</th>
                              <th className="px-6 py-3">Value</th>
                              <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedClient.jobs && selectedClient.jobs.length > 0 ? (
                              selectedClient.jobs.map((job) => (
                                <tr key={job.id} onClick={() => navigate(jobPath(selectedClient.id, job.id))}
                                  className="bg-white border-b border-gray-200 hover:bg-primary/5 cursor-pointer">
                                  <td className="px-6 py-4 font-semibold text-primary">{job.job_number}</td>
                                  <td className="px-6 py-4 font-medium text-gray-900 hover:text-primary">{job.title}</td>
                                  <td className="px-6 py-4">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(job.status)}`}>
                                      {formatStatus(job.status)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">{formatDate(job.start_date)}</td>
                                  <td className="px-6 py-4">{formatCurrency(job.total_price)}</td>
                                  <td className="px-6 py-4 text-right">
                                    <button
                                      onClick={() => navigate(jobPath(selectedClient.id, job.id))}
                                      className="font-medium text-primary hover:underline"
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={6} className="px-6 py-8 text-center">
                                  <span className="material-symbols-outlined text-gray-300 text-4xl block mb-2">work_off</span>
                                  <p className="text-sm text-gray-500 mb-3">No jobs yet for this client</p>
                                  {/* empty-state CTA (#10) */}
                                  <button
                                    onClick={() => navigate('/jobs')}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                                  >
                                    <span className="material-symbols-outlined text-base">add</span>
                                    Create First Job
                                  </button>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </DetailSection>

                    {/* Notes Section */}
                    <DetailSection title="Notes" icon="note" sectionKey="notes" activeKey={activeSection} onActivate={handleTreeSelect}>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedClient.notes || 'No notes available'}</p>
                    </DetailSection>

                    {/* Quotes Section */}
                    <DetailSection title="Quotes" icon="description" sectionKey="quotes" activeKey={activeSection} onActivate={handleTreeSelect}>
                      <div className="mb-4 flex gap-2">
                        <button
                          onClick={() => loadQuotes(selectedClient?.id || '')}
                          disabled={loadingQuotes}
                          className="px-3 py-2 text-sm border border-gray-200 text-gray-900 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          {loadingQuotes ? 'Loading...' : 'Refresh'}
                        </button>
                        <button 
                          onClick={() => setShowEstimateSelectorForQuote(true)}
                          disabled={estimates.length === 0 && labourEstimates.length === 0}
                          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                          title={(estimates.length === 0 && labourEstimates.length === 0) ? 'No estimates available - create an estimate first' : 'Create quote from existing estimates'}
                        >
                          <span className="material-symbols-outlined text-base">add</span>
                          <span>Create Quote</span>
                        </button>
                      </div>
                      {loadingQuotes ? (
                        <div className="p-8 text-center">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          <p className="mt-2 text-gray-600">Loading quotes...</p>
                        </div>
                      ) : quotes.length === 0 ? (
                        <div className="p-6 text-center text-gray-600">
                          No quotes available for this client
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm text-left">
                            <thead className="text-xs uppercase bg-gray-50 text-gray-600">
                              <tr>
                                <th className="px-6 py-3">Quote #</th>
                                <th className="px-6 py-3">Job</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3">Total</th>
                                <th className="px-6 py-3">Date</th>
                                <th className="px-6 py-3 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {quotes.map((quote) => (
                                <tr key={quote.id} className="bg-white border-b border-gray-200 hover:bg-primary/5">
                                  <td className="px-6 py-4 font-semibold text-gray-900">
                                    {quote.quote_number}
                                  </td>
                                  <td className="px-6 py-4">
                                    {quote.job_title || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      quote.status === 'Approved' ? 'bg-green-100 text-green-800' :
                                      quote.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                                      quote.status === 'Draft' ? 'bg-yellow-100 text-yellow-800' :
                                      quote.status === 'Declined' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {quote.status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 font-semibold">
                                    ${Number(quote.total || 0).toFixed(2)}
                                  </td>
                                  <td className="px-6 py-4">
                                    {quote.created_at ? new Date(quote.created_at).toLocaleDateString() : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 text-right">
                                    <button
                                      onClick={() => handleOpenQuoteBuilder(quote.id.toString())}
                                      className="font-medium text-primary hover:underline"
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </DetailSection>

                    {/* Estimates Section */}
                    <DetailSection title="Estimates" icon="assessment" sectionKey="estimates" activeKey={activeSection} onActivate={handleTreeSelect}>
                      {/* Material Estimates */}
                      <div className="bg-white rounded-xl border border-gray-200">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                          <h3 className="text-lg font-bold text-gray-900">
                            Material Estimates ({estimates.length})
                          </h3>
                          <div className="flex gap-2">
                            <button
                              onClick={() => loadEstimates(selectedClient?.id || '')}
                              disabled={loadingEstimates}
                              className="px-3 py-2 text-sm border border-gray-200 text-gray-900 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                              {loadingEstimates ? 'Loading...' : 'Refresh'}
                            </button>
                            <button
                              onClick={() => setShowMaterialModal(true)}
                              className="px-3 py-2 text-sm bg-primary text-white rounded-lg hover:opacity-90 flex items-center gap-1"
                            >
                              <span className="material-symbols-outlined text-base">add</span>
                              New Material Estimate
                            </button>
                          </div>
                        </div>
                        <div className="p-6">
                          {loadingEstimates ? (
                            <p className="text-gray-600">Loading estimates...</p>
                          ) : estimates.length === 0 ? (
                            <p className="text-gray-600">No material estimates available for this client</p>
                          ) : (
                            <div className="space-y-3">
                              {estimates.map((estimate: any) => (
                                <div key={estimate.id} className="p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h4 className="font-semibold text-gray-900">
                                        EST-{estimate.id.toString().padStart(5, '0')}
                                      </h4>
                                      <p className="text-sm text-gray-600 mt-1">{estimate.title}</p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Job: {estimate.job_title || 'N/A'}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-bold text-gray-900">
                                        ${Number(estimate.total_amount).toFixed(2)}
                                      </p>
                                      <p className="text-xs text-gray-600 mt-1 capitalize">
                                        {estimate.status}
                                      </p>
                                      <button
                                        onClick={() => navigate(estimatePath(selectedClient.id, estimate.job_id, estimate.id))}
                                        className="mt-2 text-sm font-medium text-primary hover:underline"
                                      >
                                        View Details
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Labour Estimates */}
                      <div className="bg-white rounded-xl border border-gray-200 mt-4">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                          <h3 className="text-lg font-bold text-gray-900">
                            Labour Estimates ({labourEstimates.length})
                          </h3>
                          <div className="flex gap-2">
                            <button
                              onClick={() => loadLabourEstimates(selectedClient?.id || '')}
                              disabled={loadingLabourEstimates}
                              className="px-3 py-2 text-sm border border-gray-200 text-gray-900 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                              {loadingLabourEstimates ? 'Loading...' : 'Refresh'}
                            </button>
                            <button
                              onClick={() => setShowLabourModal(true)}
                              className="px-3 py-2 text-sm bg-primary text-white rounded-lg hover:opacity-90 flex items-center gap-1"
                            >
                              <span className="material-symbols-outlined text-base">add</span>
                              New Labour Estimate
                            </button>
                          </div>
                        </div>
                        <div className="p-6">
                          {loadingLabourEstimates ? (
                            <p className="text-gray-600">Loading labour estimates...</p>
                          ) : labourEstimates.length === 0 ? (
                            <p className="text-gray-600">No labour estimates available for this client</p>
                          ) : (
                            <div className="space-y-3">
                              {labourEstimates.map((estimate: any) => (
                                <div key={estimate.id} className="p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h4 className="font-semibold text-gray-900">
                                        LAB-{estimate.id.toString().padStart(5, '0')}
                                      </h4>
                                      <p className="text-sm text-gray-600 mt-1">{estimate.title}</p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Job: {estimate.job_title || 'N/A'} • {estimate.total_hours} hrs @ ${estimate.hourly_rate}/hr
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-bold text-gray-900">
                                        ${Number(estimate.total_amount).toFixed(2)}
                                      </p>
                                      <p className="text-xs text-gray-600 mt-1 capitalize">
                                        {estimate.status}
                                      </p>
                                      <button
                                        onClick={() => navigate(labourEstimatePath(selectedClient.id, estimate.job_id, estimate.id))}
                                        className="mt-2 text-sm font-medium text-primary hover:underline"
                                      >
                                        View Details
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </DetailSection>

                    {/* Invoices Section */}
                    <DetailSection title="Invoices" icon="receipt" sectionKey="invoices" activeKey={activeSection} onActivate={handleTreeSelect}>
                      <div className="mb-4 flex gap-2">
                        <button
                          onClick={() => loadInvoices(selectedClient?.id || '')}
                          disabled={loadingInvoices}
                          className="px-3 py-2 text-sm border border-gray-200 text-gray-900 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          {loadingInvoices ? 'Loading...' : 'Refresh'}
                        </button>
                        <button
                          onClick={handleCreateInvoice}
                          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 transition-opacity"
                        >
                          <span className="material-symbols-outlined text-base">add</span>
                          <span>Create Invoice</span>
                        </button>
                      </div>
                      {loadingInvoices ? (
                        <div className="p-8 text-center">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          <p className="mt-2 text-gray-600">Loading invoices...</p>
                        </div>
                      ) : invoices.length === 0 ? (
                        <div className="p-6 text-center text-gray-600">
                          No invoices available for this client.
                          <div className="mt-3">
                            <button
                              onClick={handleCreateInvoice}
                              className="text-primary hover:underline font-medium"
                            >
                              Create your first invoice
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm text-left">
                            <thead className="text-xs uppercase bg-gray-50 text-gray-600">
                              <tr>
                                <th className="px-6 py-3">Invoice #</th>
                                <th className="px-6 py-3">Job</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3">Total</th>
                                <th className="px-6 py-3">Balance</th>
                                <th className="px-6 py-3">Due Date</th>
                                <th className="px-6 py-3 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {invoices.map((invoice) => (
                                <tr key={invoice.id} className="bg-white border-b border-gray-200 hover:bg-primary/5">
                                  <td className="px-6 py-4 font-semibold text-gray-900">
                                    {invoice.invoice_number}
                                  </td>
                                  <td className="px-6 py-4">
                                    {invoice.job_title || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4">
                                    <InvoiceStatusBadge status={invoice.status} size="sm" />
                                  </td>
                                  <td className="px-6 py-4 font-semibold">
                                    ${Number(invoice.total || 0).toFixed(2)}
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className={invoice.balance_due > 0 ? 'text-orange-600 font-medium' : 'text-green-600'}>
                                      ${Number(invoice.balance_due || 0).toFixed(2)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 text-right">
                                    <button
                                      onClick={() => navigate(invoicePath(invoice))}
                                      className="font-medium text-primary hover:underline"
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {/* Show approved quotes that can be converted to invoices */}
                      {quotes.filter(q => q.status === 'Approved').length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Create Invoice from Approved Quote</h4>
                          <div className="space-y-2">
                            {quotes.filter(q => q.status === 'Approved').map((quote) => (
                              <div key={quote.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div>
                                  <span className="font-medium text-gray-900">{quote.quote_number}</span>
                                  <span className="ml-2 text-gray-600">- {quote.job_title || 'N/A'}</span>
                                  <span className="ml-2 font-semibold text-gray-900">${Number(quote.total || 0).toFixed(2)}</span>
                                </div>
                                <button
                                  onClick={() => handleCreateInvoiceFromQuote(quote.id.toString())}
                                  className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                                >
                                  Create Invoice
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </DetailSection>

                    {/* Documents Section */}
                    <DetailSection title="Documents" icon="article" sectionKey="documents" activeKey={activeSection} onActivate={handleTreeSelect}>
                      {/* CR #121: every doc for this client — their own uploads
                          plus everything attached to their jobs */}
                      <ClientDocumentsSection clientId={String(selectedClient.id)} />
                    </DetailSection>

                    {/* Portal Usage (CR #175) — real portal traffic, previews excluded */}
                    <DetailSection title="Portal Usage" icon="monitoring" sectionKey="usage" activeKey={activeSection} onActivate={handleTreeSelect}>
                      <PortalUsagePanel kind="client" id={Number(selectedClient.id)} />
                    </DetailSection>
                    </div>
                  </div>
                </>
            ) : (
              <div className="px-1.5 py-3 md:p-6">
                <ClientsTable
                  clients={filteredClients}
                  loading={loading}
                  onOpen={handleClientClick}
                  onEdit={c => handleOpenModal(c as Client)}
                  onDelete={handleDelete}
                  onAdd={() => handleOpenModal()}
                  onExport={exportClientsCsv}
                  onInvite={async c => {
                    if (!c.email) { toast.error(`${c.name} has no email address — add one first.`); return; }
                    try {
                      const invite = await createInvite({
                        invite_type: 'client',
                        email: c.email,
                        target_client_id: Number(c.id),
                        is_reset: true, // new email registers; existing account gets a password reset
                        expires_days: 7,
                      });
                      const url = invite.inviteUrl || `${window.location.origin}/portal/register?code=${invite.code}`;
                      await navigator.clipboard.writeText(url).catch(() => {});
                      toast.success(`Portal invite emailed to ${c.email} — link also copied.`);
                    } catch {
                      toast.error('Failed to send invite');
                    }
                  }}
                />
              </div>
            )}
          </div>

        {/* Add/Edit Client Modal */}
        {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={handleCloseModal}
            />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="px-6 pt-5 pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                      {editingClient ? 'Edit Client' : 'Add New Client'}
                    </h3>
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Contact Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Address
                      </label>
                      <textarea
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 flex gap-3 justify-between">
                  {editingClient && (
                    <button
                      type="button"
                      onClick={() => {
                        handleDelete(editingClient.id);
                        handleCloseModal();
                      }}
                      className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                    >
                      Delete Client
                    </button>
                  )}
                  <div className="flex gap-3 ml-auto">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-4 py-2 text-sm font-medium text-gray-900 border border-gray-200 rounded-lg hover:bg-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90"
                    >
                      {editingClient ? 'Update Client' : 'Create Client'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Labour Estimate Modal */}
      {showLabourModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 md:p-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  New Labour Estimate
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Create a labour estimate for {selectedClient?.company || selectedClient?.name}
                </p>
              </div>
              <button
                onClick={() => setShowLabourModal(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={labourFormData.title}
                  onChange={(e) => setLabourFormData({ ...labourFormData, title: e.target.value })}
                  placeholder="e.g., Framing Labour - Main Floor"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job <span className="text-red-500">*</span>
                </label>
                <select
                  value={labourFormData.job_id}
                  onChange={(e) => setLabourFormData({ ...labourFormData, job_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">Select a job...</option>
                  {selectedClient?.jobs?.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.job_number} - {job.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Hours <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={labourFormData.total_hours}
                    onChange={(e) => setLabourFormData({ ...labourFormData, total_hours: e.target.value })}
                    placeholder="e.g., 40"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hourly Rate ($) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={labourFormData.hourly_rate}
                    onChange={(e) => setLabourFormData({ ...labourFormData, hourly_rate: e.target.value })}
                    placeholder="e.g., 75"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              {labourFormData.total_hours && labourFormData.hourly_rate && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Estimated Total:</span>
                    <span className="text-xl font-bold text-primary">
                      ${(parseFloat(labourFormData.total_hours) * parseFloat(labourFormData.hourly_rate)).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={labourFormData.notes}
                  onChange={(e) => setLabourFormData({ ...labourFormData, notes: e.target.value })}
                  placeholder="Additional notes about the labour estimate..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowLabourModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateLabourEstimate}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
                >
                  Create Estimate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Material Estimate Modal */}
      {showMaterialModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 md:p-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  New Material Estimate
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Create a material estimate for {selectedClient?.company || selectedClient?.name}
                </p>
              </div>
              <button
                onClick={() => setShowMaterialModal(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={materialFormData.title}
                  onChange={(e) => setMaterialFormData({ ...materialFormData, title: e.target.value })}
                  placeholder="e.g., Framing Materials - Main Floor"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job <span className="text-red-500">*</span>
                </label>
                <select
                  value={materialFormData.job_id}
                  onChange={(e) => setMaterialFormData({ ...materialFormData, job_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">Select a job...</option>
                  {selectedClient?.jobs?.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.job_number} - {job.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Materials <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addMaterialItem}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    <span className="material-symbols-outlined text-base">add</span>
                    Add Item
                  </button>
                </div>
                <div className="space-y-3">
                  {materialFormData.items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateMaterialItem(index, 'name', e.target.value)}
                        placeholder="Material name"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      />
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateMaterialItem(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateMaterialItem(index, 'unit_price', e.target.value)}
                        placeholder="Price"
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      />
                      <div className="w-24 px-3 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-900">
                        ${((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                      </div>
                      {materialFormData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeMaterialItem(index)}
                          className="p-2 text-gray-400 hover:text-red-500"
                        >
                          <span className="material-symbols-outlined text-base">delete</span>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {materialFormData.items.some(item => item.quantity && item.unit_price) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Estimated Total:</span>
                    <span className="text-xl font-bold text-primary">
                      ${materialFormData.items.reduce((sum, item) => 
                        sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)), 0
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={materialFormData.notes}
                  onChange={(e) => setMaterialFormData({ ...materialFormData, notes: e.target.value })}
                  placeholder="Additional notes about the material estimate..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowMaterialModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateMaterialEstimate}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
                >
                  Create Estimate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estimate Selector for Quote Modal */}
      {showEstimateSelectorForQuote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 md:p-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Create Quote from Estimates
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Select a job and choose estimates to include in the quote
                </p>
              </div>
              <button
                onClick={() => {
                  setShowEstimateSelectorForQuote(false);
                  setSelectedEstimateIds([]);
                  setSelectedJobIdForQuote('');
                }}
                disabled={creatingQuoteFromEstimate}
                className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-6">
              {getUniqueJobs().length > 0 ? (
                <div className="space-y-4">
                  {/* Job Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Job
                    </label>
                    <select
                      value={selectedJobIdForQuote}
                      onChange={(e) => {
                        setSelectedJobIdForQuote(e.target.value);
                        setSelectedEstimateIds([]);
                        setSelectedLabourEstimateIds([]);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="">Choose a job...</option>
                      {getUniqueJobs().map((job: { id: string; title: string }) => (
                        <option key={job.id} value={job.id}>
                          {job.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Material Estimates for Selected Job */}
                  {selectedJobIdForQuote && getEstimatesForJob(selectedJobIdForQuote).length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Material Estimates ({getEstimatesForJob(selectedJobIdForQuote).length} available)
                      </label>
                      <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
                        {getEstimatesForJob(selectedJobIdForQuote).map((estimate: any) => (
                          <label
                            key={estimate.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedEstimateIds.includes(estimate.id)
                                ? 'border-primary bg-primary/5'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedEstimateIds.includes(estimate.id)}
                              onChange={() => toggleEstimateSelection(estimate.id)}
                              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">
                                EST-{estimate.id.toString().padStart(5, '0')} - {estimate.title}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                  estimate.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                  estimate.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {estimate.status?.charAt(0).toUpperCase() + estimate.status?.slice(1)}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-900">
                                ${Number(estimate.total_amount).toFixed(2)}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Labour Estimates for Selected Job */}
                  {selectedJobIdForQuote && getLabourEstimatesForJob(selectedJobIdForQuote).length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Labour Estimates ({getLabourEstimatesForJob(selectedJobIdForQuote).length} available)
                      </label>
                      <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
                        {getLabourEstimatesForJob(selectedJobIdForQuote).map((estimate: any) => (
                          <label
                            key={estimate.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedLabourEstimateIds.includes(estimate.id)
                                ? 'border-primary bg-primary/5'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedLabourEstimateIds.includes(estimate.id)}
                              onChange={() => toggleLabourEstimateSelection(estimate.id)}
                              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">
                                LAB-{estimate.id.toString().padStart(5, '0')} - {estimate.title}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                <span className="text-xs">{estimate.total_hours} hrs @ ${Number(estimate.hourly_rate).toFixed(2)}/hr</span>
                                <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                  estimate.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                  estimate.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {estimate.status?.charAt(0).toUpperCase() + estimate.status?.slice(1)}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-900">
                                ${Number(estimate.total_amount).toFixed(2)}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Selected Total */}
                  {(selectedEstimateIds.length > 0 || selectedLabourEstimateIds.length > 0) && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">
                          {selectedEstimateIds.length + selectedLabourEstimateIds.length} estimate{(selectedEstimateIds.length + selectedLabourEstimateIds.length) > 1 ? 's' : ''} selected
                        </span>
                        <span className="text-xl font-bold text-primary">
                          ${(
                            getEstimatesForJob(selectedJobIdForQuote)
                              .filter((est: any) => selectedEstimateIds.includes(est.id))
                              .reduce((sum: number, est: any) => sum + Number(est.total_amount), 0) +
                            getLabourEstimatesForJob(selectedJobIdForQuote)
                              .filter((est: any) => selectedLabourEstimateIds.includes(est.id))
                              .reduce((sum: number, est: any) => sum + Number(est.total_amount), 0)
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => {
                        setShowEstimateSelectorForQuote(false);
                        setSelectedEstimateIds([]);
                        setSelectedLabourEstimateIds([]);
                        setSelectedJobIdForQuote('');
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateQuoteFromEstimates}
                      disabled={creatingQuoteFromEstimate || (selectedEstimateIds.length === 0 && selectedLabourEstimateIds.length === 0)}
                      className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creatingQuoteFromEstimate ? 'Creating...' : 'Create Quote'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">
                    No estimates found for this client. Create an estimate first.
                  </p>
                  <button
                    onClick={() => setShowEstimateSelectorForQuote(false)}
                    className="px-4 py-2 border border-gray-200 text-gray-900 rounded-lg font-medium hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quote Builder Modal */}
      {selectedQuoteId && (
        <QuoteBuilderModal
          quoteId={selectedQuoteId}
          isOpen={showQuoteBuilder}
          onClose={handleCloseQuoteBuilder}
          onSaved={() => {
            if (selectedClient?.id) {
              loadQuotes(selectedClient.id);
            }
          }}
        />
      )}

      {/* Invoice Create/Edit Modal */}
      {selectedClient && (
        <InvoiceModal
          isOpen={showInvoiceModal}
          onClose={() => {
            setShowInvoiceModal(false);
            setEditingInvoice(null);
          }}
          onSave={handleSaveInvoice}
          invoice={editingInvoice || undefined}
          clientId={parseInt(selectedClient.id)}
          clientName={selectedClient.name}
          jobs={selectedClient.jobs?.map(job => ({ id: parseInt(job.id), title: job.title })) || []}
        />
      )}

      {/* Invoice Detail Modal */}
      <InvoiceDetailModal
        isOpen={showInvoiceDetailModal}
        onClose={() => {
          setShowInvoiceDetailModal(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onEdit={handleEditInvoice}
        onSend={handleSendInvoice}
        onRecordPayment={handleRecordPayment}
        onDelete={handleDeleteInvoice}
        onRefresh={(updated) => setSelectedInvoice(updated)}
      />

      {/* Record Payment Modal */}
      <RecordPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSubmit={handleSubmitPayment}
        invoice={selectedInvoice}
      />

      {confirmDeleteInvoice && (
        <ConfirmDialog
          title="Delete Invoice"
          message="Are you sure you want to delete this invoice? This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteInvoiceAction}
          onCancel={() => setConfirmDeleteInvoice(false)}
        />
      )}

      {confirmDeleteClientId && (
        <ConfirmDialog
          title="Delete Client"
          message="Are you sure you want to delete this client? This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteClientAction}
          onCancel={() => setConfirmDeleteClientId(null)}
        />
      )}
      </div>
    </MainLayout>
  );
};
